/**
 * Payment-request link validation (offline deal flow).
 *
 * Sellers can share their own Tikkie / bank payment-request link with the
 * buyer. Only links from known Dutch payment-request providers are accepted,
 * so arbitrary URLs can never be rendered as a trusted "pay" button
 * (anti-phishing). The platform never touches the money itself.
 */

// Dedicated payment-request hosts only. Deliberately excludes general banking
// hosts (e.g. www.ing.nl): links here render as a prominent "pay" button, so
// the surface must be limited to hosts whose sole purpose is payment requests.
const ALLOWED_PAYMENT_LINK_HOSTS = [
  'tikkie.me',
  'betaalverzoek.rabobank.nl',
  'betaalverzoek.abnamro.nl',
  'betaalverzoek.ing.nl',
  'bunq.me',
];

/**
 * Validate and normalize a payment-request link.
 * @returns The normalized https URL, or null when the link is not allowed.
 */
export const validatePaymentLink = (input: string): string | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Users often paste links without a scheme.
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:') return null;

  const host = url.hostname.toLowerCase();
  const allowed = ALLOWED_PAYMENT_LINK_HOSTS.some(
    (allowedHost) => host === allowedHost || host.endsWith(`.${allowedHost}`)
  );

  return allowed ? url.toString() : null;
};
